import { useEffect, useRef } from 'react';

interface UseStickyWithinContainerOptions {
  /**
   * Offset from top when in sticky mode (in pixels)
   * @default 32
   */
  stickyOffset?: number;
  /**
   * Fixed width to apply when in sticky mode (in pixels)
   * If not provided, will use the natural width of the element
   */
  fixedWidth?: number;
  /**
   * Minimum screen width for sticky behavior to activate
   * @default 1024
   */
  minScreenWidth?: number;
  /**
   * Whether to enable the sticky behavior
   * @default true
   */
  enabled?: boolean;
}

/**
 * Custom hook for creating a sticky element that stays within its container bounds
 *
 * Originally created for the dispensary location page sidebar, but designed to be reusable
 * for any scenario where you need a sticky element that doesn't move beyond its container.
 *
 * @param options Configuration options for the sticky behavior
 * @returns Object containing refs for the sticky element and its container
 *
 * @example
 * ```tsx
 * const { stickyRef, containerRef } = useStickyWithinContainer({
 *   stickyOffset: 32,
 *   fixedWidth: 350,
 *   minScreenWidth: 1024
 * });
 *
 * return (
 *   <div ref={containerRef} className="relative">
 *     <div className="main-content">...</div>
 *     <div ref={stickyRef} className="sidebar">...</div>
 *   </div>
 * );
 * ```
 */
export function useStickyWithinContainer(options: UseStickyWithinContainerOptions = {}) {
  const { stickyOffset = 32, fixedWidth, minScreenWidth = 1024, enabled = true } = options;

  const stickyRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentState = useRef<'static' | 'fixed' | 'bottom'>('static');

  useEffect(() => {
    if (!enabled) return;

    const handlePosition = () => {
      if (!stickyRef.current || !containerRef.current || window.innerWidth < minScreenWidth) {
        return;
      }

      const stickyElement = stickyRef.current;
      const container = containerRef.current;
      const stickyHeight = stickyElement.offsetHeight;

      // Get the container's position
      const containerRect = container.getBoundingClientRect();
      const scrollTop = window.pageYOffset;

      // Calculate positions
      const containerTop = containerRect.top + scrollTop;
      const containerBottom = containerRect.bottom + scrollTop;
      const viewportTop = scrollTop;

      // Three-state logic: static, fixed, or bottom-anchored
      let targetState: 'static' | 'fixed' | 'bottom' = 'static';

      // Determine which state we should be in
      if (viewportTop + stickyOffset < containerTop) {
        // Above the container - scroll naturally
        targetState = 'static';
      } else if (viewportTop + stickyOffset + stickyHeight <= containerBottom) {
        // Within container bounds - stick to top
        targetState = 'fixed';
      } else {
        // Container extends beyond sticky space - anchor to bottom
        targetState = 'bottom';
      }

      // Only change state if it's actually different
      if (currentState.current !== targetState) {
        currentState.current = targetState;

        if (targetState === 'fixed') {
          // Fixed to viewport - preserve natural width and position
          // First reset to get natural position
          stickyElement.style.position = 'static';
          const naturalRect = stickyElement.getBoundingClientRect();

          // Apply fixed positioning at natural location
          stickyElement.style.position = 'fixed';
          stickyElement.style.top = `${stickyOffset}px`;
          stickyElement.style.left = `${naturalRect.left}px`;
          stickyElement.style.width = fixedWidth ? `${fixedWidth}px` : `${naturalRect.width}px`;
          stickyElement.style.bottom = 'auto';
          stickyElement.style.right = 'auto';
          stickyElement.style.zIndex = '10';
        } else if (targetState === 'bottom') {
          // Absolutely positioned at bottom of container
          stickyElement.style.position = 'absolute';
          stickyElement.style.top = 'auto';
          stickyElement.style.bottom = '0';
          stickyElement.style.left = 'auto';
          stickyElement.style.right = '0';
          stickyElement.style.width = fixedWidth ? `${fixedWidth}px` : 'auto';
          stickyElement.style.zIndex = '10';
        } else {
          // Normal positioning - let it scroll naturally
          stickyElement.style.position = '';
          stickyElement.style.top = '';
          stickyElement.style.bottom = '';
          stickyElement.style.left = '';
          stickyElement.style.right = '';
          stickyElement.style.width = '';
          stickyElement.style.zIndex = '';
        }
      }
    };

    // Throttled scroll handler
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handlePosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handlePosition);

    // Initial positioning
    handlePosition();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handlePosition);

      // Cleanup positioning on unmount
      if (stickyRef.current) {
        const element = stickyRef.current;
        element.style.position = '';
        element.style.top = '';
        element.style.bottom = '';
        element.style.left = '';
        element.style.right = '';
        element.style.width = '';
        element.style.zIndex = '';
      }

      // Reset state tracking
      currentState.current = 'static';
    };
  }, [enabled, stickyOffset, fixedWidth, minScreenWidth]);

  return {
    stickyRef,
    containerRef,
  };
}
